import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Spin,
  Space,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  DollarOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const EditCurrency = () => {
  const { curr_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const fetchCurrencyDetails = useCallback(async () => {
    if (!curr_id) {
      notify.error({ message: "Currency ID is required" });
      navigate("/currencies");
      return;
    }

    setLoadingData(true);
    try {
      const response = await masterService.getSingleCurrency(curr_id);

      if (response.data && response.data.status) {
        const currencyData = response.data.data[0] || response.data.data;
        if (currencyData && currencyData.curr_name) {
          form.setFieldsValue({
            curr_name: currencyData.curr_name,
            curr_code: currencyData.curr_code,
            curr_html_code: currencyData.curr_html_code,
            curr_symbol: currencyData.curr_symbol,
            curr_is_active: currencyData.curr_is_active || "Y",
          });
        } else {
          notify.error({ message: "Currency data not found" });
          navigate("/currencies");
        }
      } else {
        notify.error({
          message: response.data?.message || "Failed to fetch currency details",
        });
        navigate("/currencies");
      }
    } catch (error) {
      console.error("Error fetching currency details:", error);
      notify.error({ message: "Error fetching currency details" });
      navigate("/currencies");
    } finally {
      setLoadingData(false);
    }
  }, [curr_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Currency");
    fetchCurrencyDetails();
  }, [fetchCurrencyDetails, setPageTitle]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        curr_id: curr_id,
      };

      const response = await masterService.updateCurrency(curr_id, payload);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Currency updated successfully",
        });
        navigate("/currencies");
      } else {
        notify.error({
          message: response.data.message || "Failed to update currency",
        });
      }
    } catch (error) {
      console.error("Error updating currency:", error);
      notify.error({ message: "Error updating currency" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/currencies");
  };

  return (
    <div className="min-h-screen">
      <Spin spinning={loadingData}>
        <Card
          className="shadow-lg border-0"
          bodyStyle={{
            padding: "32px",
            backgroundColor: "#ffffff",
            borderColor: "#d9d9d9",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <DollarOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Currency Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Currency Name
                      </span>
                    }
                    name="curr_name"
                    rules={[
                      { required: true, message: "Please enter currency name" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter currency name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Currency Code
                      </span>
                    }
                    name="curr_code"
                    rules={[
                      { required: true, message: "Please enter currency code" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter currency code (e.g., USD, EUR)"
                      className="rounded-lg"
                      style={{ textTransform: "uppercase" }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="curr_is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Currency Details Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <SettingOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Currency Details
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Currency Symbol
                      </span>
                    }
                    name="curr_symbol"
                    rules={[
                      { required: true, message: "Please enter currency symbol" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter currency symbol (e.g., $, €, ₹)"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        HTML Code
                      </span>
                    }
                    name="curr_html_code"
                    rules={[
                      { required: true, message: "Please enter currency HTML code" },
                    ]}
                  >
                    <Input
                      placeholder="Enter HTML code (e.g., &#36;, &#8364;)"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Updating..." : "Update Currency"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditCurrency;
